import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { persistenceService, Entry } from '../utils/persistenceService';
import CreateEntryModal from './CreateEntryModal';

const Entries: React.FC = () => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await persistenceService.getEntries({ limit: 50 });
      setEntries(result.entries);
    } catch (err) {
      console.error('Failed to load entries:', err);
      setError('Failed to load entries. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, { 
        weekday: 'long', 
        year: 'numeric',
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatContent = (entry: Entry) => {
    // For check-in entries, show a summary
    if (entry.type === 'checkin' && entry.content.responses) {
      const priority = entry.content.responses[0] || '';
      const obstacles = entry.content.responses[1] || '';
      const nextStep = entry.content.responses[2] || '';
      
      return (
        <div className="space-y-2">
          {priority && (
            <div>
              <span className="font-medium text-gray-700">Priority: </span>
              <span className="text-gray-600">"{priority}"</span>
            </div>
          )}
          {obstacles && (
            <div>
              <span className="font-medium text-gray-700">Challenges: </span>
              <span className="text-gray-600">"{obstacles}"</span>
            </div>
          )}
          {nextStep && (
            <div>
              <span className="font-medium text-gray-700">Next step: </span>
              <span className="text-gray-600">"{nextStep}"</span>
            </div>
          )}
        </div>
      );
    }

    // For manual entries, show the body
    return (
      <div className="text-gray-600">
        "{entry.content.body}"
      </div>
    );
  };

  const handleCreateEntry = async (entryData: Omit<Entry, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newEntry = await persistenceService.createEntry(entryData);
      // Add to the beginning of the list (newest first)
      setEntries(prev => [newEntry, ...prev]);
    } catch (error) {
      console.error('Failed to create entry:', error);
      throw error; // Re-throw to let modal handle the error
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto px-2 py-4 flex flex-col gap-6">
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Log</h2>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
          <span className="ml-2 text-gray-500">Loading your log...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-2xl mx-auto px-2 py-4 flex flex-col gap-6">
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Log</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800">{error}</div>
          <button 
            onClick={loadEntries}
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="w-full max-w-2xl mx-auto px-2 py-4 flex flex-col gap-6">
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Log</h2>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <div className="text-gray-500 mb-2">No log entries yet</div>
          <div className="text-gray-400 text-sm">
            Complete your daily check-ins to start building your momentum log.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-2 py-4 flex flex-col gap-6 relative">
      <h2 className="text-2xl font-bold text-gray-700 mb-2">Entries</h2>
      <div className="grid grid-cols-1 gap-4">
        {entries.map((entry) => (
          <div key={entry._id} className="bg-white rounded-xl shadow p-6 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-800">
                {formatDate(entry.date)}
              </div>
              <div className="flex items-center gap-2">
                {entry.metadata.checkinType && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {entry.metadata.checkinType}
                  </span>
                )}
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  {entry.type}
                </span>
              </div>
            </div>
            <div>{formatContent(entry)}</div>
            {entry.metadata.wordCount && (
              <div className="text-xs text-gray-400 mt-2">
                {entry.metadata.wordCount} words
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Floating Add Button */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="fixed bottom-24 right-6 w-14 h-14 bg-rose-500 hover:bg-rose-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-40"
        aria-label="Create new entry"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Create Entry Modal */}
      <CreateEntryModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateEntry}
      />
    </div>
  );
};

export default Entries; 